/**
 * Whether a stored registration session conflicts with the `?team_id=` link
 * the reader just followed.
 *
 * Decided where a session RESUMES (`index.jsx`), not on the start step: a
 * stored session can be routed straight to register-entity, payment or the
 * waiver, so a check that only ran on the start step would never see those.
 *
 * Only a session with something to lose is worth interrupting for — a team of
 * its own that differs from the link, or progress past the start step. One
 * still on the start step with no team is simply filled in by the link.
 * One that already holds the linked team past the start step is resumed with
 * the link dropped — it has been acted on.
 *
 * A session registering a TEAM is the exception: a `team_id` link is a player
 * registration, so a rep's session conflicts with it wherever it has got to,
 * whatever team it holds. A rep who has not named their team yet looks empty
 * by every other measure here, and would otherwise be overwritten silently.
 * A session with no role chosen yet is still just an empty session.
 */

import type { RegistrationSteps } from '../../../types'

/** What `index.jsx` does with the stored session. */
export type SessionLinkConflict =
  /** Apply it as usual — no link, or nothing to lose. */
  | 'none'
  /** Hold it back and ask whether to start over with the link. */
  | 'prompt'
  /**
   * Apply it, but drop the link first: the session already holds the linked
   * team and has moved past the start step, so the link has been acted on.
   * Left in the URL it would ride along to the step the session resumes on,
   * and send Back straight into an auto-advance.
   */
  | 'consume'

/** The stored-session fields this decision reads. */
export interface ConflictSession {
  team_id?: string | null
  registration_step?: string | null
  /** Which role the session registers as — matched against the team-official type. */
  registration_type_id?: string | number | null
}

/**
 * A server `registration_step` value. Typed from the enum so a misspelt step
 * below fails type-checking, without loading `common/types` at runtime.
 */
type ServerStep = `${RegistrationSteps}`

/**
 * Server steps past the start step. A session on one of these has work the
 * reader would lose by starting over, even when it holds no team.
 *
 * Shared with `sessionInviteConflict`, which asks the same question of an
 * `?invite=` link.
 */
export const PROGRESSED_STEPS: ReadonlySet<string> = new Set<ServerStep>([
  'team_register',
  'payment',
  'confirm',
  'waiver',
])

export const sessionLinkConflict = ({
  urlTeamId,
  session,
  teamOfficialTypeId,
}: {
  urlTeamId: string | null | undefined
  session: ConflictSession | null | undefined
  /**
   * The team-official registration type's id, when the types have loaded.
   * Without it a rep session cannot be identified, and nothing is prompted on
   * a guess.
   */
  teamOfficialTypeId?: string | null
}): SessionLinkConflict => {
  if (!urlTeamId || !session) {
    return 'none'
  }
  // A `team_id` link is a PLAYER registration, so a session registering a TEAM
  // is a different registration entirely — it conflicts wherever it has got to.
  // This catches the rep who has not named their team yet, whose `team_id` is
  // null and whose step is still the start step: the rules below would read
  // that as an empty session and silently overwrite their work.
  //
  // Compared as strings: the id arrives as a number from the API and as a
  // string from the types endpoint, matching how `index.jsx` reads the role.
  if (
    teamOfficialTypeId &&
    session.registration_type_id != null &&
    String(session.registration_type_id) === String(teamOfficialTypeId)
  ) {
    return 'prompt'
  }
  const progressed = Boolean(
    session.registration_step && PROGRESSED_STEPS.has(session.registration_step)
  )
  // The session stores `teamIdentity(team)` — `uid ?? id` — and the payload
  // sends both keys with the same value, so a plain comparison is enough.
  if (session.team_id === urlTeamId) {
    return progressed ? 'consume' : 'none'
  }
  if (session.team_id) {
    return 'prompt'
  }
  return progressed ? 'prompt' : 'none'
}
